import React from 'react';
import type { ChatMessage } from '../../auth-config/types';
import MarkdownContent from './MarkdownContent';

interface Props {
  message: ChatMessage;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const ChatMessageBubble: React.FC<Props> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div
      className={`flex items-end gap-2 mb-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar */}
      {!isUser && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#5624d0] flex items-center justify-center shadow-md">
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 3.75a.75.75 0 000 1.5h.75v1.5a4.5 4.5 0 00-4.5 4.5v1.5H4.5a.75.75 0 000 1.5h.75v.75a.75.75 0 001.5 0V13.5h9v.75a.75.75 0 001.5 0V13.5h.75a.75.75 0 000-1.5h-.75v-1.5a4.5 4.5 0 00-4.5-4.5V5.25h.75a.75.75 0 000-1.5H9.75z"
            />
          </svg>
        </div>
      )}

      <div className={`flex flex-col max-w-[78%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
            isUser
              ? 'bg-[#5624d0] text-white rounded-br-sm'
              : 'bg-[#f0f0f5] text-gray-800 rounded-bl-sm'
          }`}
          style={{ wordBreak: 'break-word' }}
        >
          {isUser ? message.content : <MarkdownContent content={message.content} />}
        </div>

        {/* Timestamp */}
        <span className="text-[10px] text-gray-400 mt-1 px-1">
          {formatTime(message.createdAt)}
        </span>
      </div>
    </div>
  );
};

// Typing indicator bubble shown while waiting for AI reply
export const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-2 mb-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#5624d0] flex items-center justify-center shadow-md">
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.75 3.75a.75.75 0 000 1.5h.75v1.5a4.5 4.5 0 00-4.5 4.5v1.5H4.5a.75.75 0 000 1.5h.75v.75a.75.75 0 001.5 0V13.5h9v.75a.75.75 0 001.5 0V13.5h.75a.75.75 0 000-1.5h-.75v-1.5a4.5 4.5 0 00-4.5-4.5V5.25h.75a.75.75 0 000-1.5H9.75z"
        />
      </svg>
    </div>
    <div className="bg-[#f0f0f5] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5 shadow-sm">
      <span
        className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: '0ms' }}
      />
      <span
        className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: '300ms' }}
      />
    </div>
  </div>
);

export default ChatMessageBubble;
